import type { FieldValues } from 'react-hook-form';
import type { InspectorDTO } from 'src/types/dto';
import React from 'react';
import { useUserGeoFromIp } from 'src/hooks/use-user-geo-from-ip';
import { shouldBlockGoogleMapsMount } from 'src/utils/user-geo-from-ip-state';

import { UniversalLocationMap } from './universal-location-map';
import { UniversalLocationMapGoogle } from './universal-location-map-google';
import type { LocationMapProps as OriginalLocationMapProps, Marker as OriginalMarker } from './types';


export interface LocationMapProps<T extends FieldValues> extends OriginalLocationMapProps<T> {
  onChange?: (value: string) => void;
  highlightedMarkerId?: number;
  selectedIds?: number[];
  selectedMarker?: InspectorDTO | null;
  onMarkerSelect?: (inspector: InspectorDTO | null) => void;
  onInspectorSelect?: (inspectorId: number) => void;
  hideSearchInput?: boolean;
  markers?: Marker[];
}

export interface Marker extends OriginalMarker {
  id?: number;
  onClick?: () => void;
}

/**
 * LocationMap component with universal map provider support
 * 
 * Automatically switches between Google Maps, Baidu Maps, or OpenStreetMap
 * based on NEXT_PUBLIC_DEFAULT_MAP_PROVIDER environment variable.
 * 
 * Features:
 * - ✅ Universal map provider support (Google/Baidu/OSM)
 * - ✅ Search bar with autocomplete
 * - ✅ Draggable markers with address updates
 * - ✅ Reverse geocoding
 * - ✅ Form integration with react-hook-form
 * - ✅ Multiple marker support for inspector selection
 * 
 * @example
 * ```tsx
 * // Basic usage - automatically uses configured provider
 * <LocationMap
 *   methods={methods}
 *   setAddressInfo={setAddressInfo}
 *   placeholderText="Search for your address..."
 *   tooltipText="Enter location"
 * />
 * 
 * <LocationMap markers={inspectorMarkers} viewOnly />
 * ```
 */
export function LocationMap<T extends FieldValues = any>(
  props: LocationMapProps<T>
) {
  const userGeo = useUserGeoFromIp();

  if (shouldBlockGoogleMapsMount(userGeo)) {
    return null;
  }

  const isUniversalMapsEnabled = process.env.NEXT_PUBLIC_ENABLE_UNIVERSAL_MAPS === 'true';

  // Use UniversalLocationMap when universal maps are enabled (supports all providers)
  if (isUniversalMapsEnabled) {
    return <UniversalLocationMap {...(props as any)} />;
  }
  
  // Fallback to Google Maps only component when universal maps are disabled
  return <UniversalLocationMapGoogle {...props} />;
}

export default LocationMap;
